import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReferralDiscountKind, ReferralDiscountStatus, ReferralStatus } from '../types';

const BADGE = 'inline-block px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap';

export const REFERRAL_DISCOUNT_KIND_LABELS: Record<ReferralDiscountKind, string> = {
  welcome: 'Welcome',
  referrer_reward: 'Referral reward',
  admin_grant: 'Admin grant',
};

export const REFERRAL_DISCOUNT_STATUS_LABELS: Record<ReferralDiscountStatus, string> = {
  available: 'Available',
  reserved: 'Reserved',
  redeemed: 'Redeemed',
  void: 'Void',
};

export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  pending: 'Not verified',
  signed_up: 'Signed up',
  active: 'Active',
};

export const ReferralKindBadge: React.FC<{ kind: ReferralDiscountKind }> = ({ kind }) => {
  if (kind === 'welcome') {
    return <span className={BADGE} style={{ background: '#fce7f3', color: '#9d174d' }}>{REFERRAL_DISCOUNT_KIND_LABELS[kind]}</span>;
  }
  if (kind === 'admin_grant') {
    return <span className={BADGE} style={{ background: '#ede9fe', color: '#5b21b6' }}>{REFERRAL_DISCOUNT_KIND_LABELS[kind]}</span>;
  }
  return <span className={`${BADGE} bg-gray-100 text-gray-800`}>{REFERRAL_DISCOUNT_KIND_LABELS[kind]}</span>;
};

export const ReferralDiscountStatusBadge: React.FC<{ status: ReferralDiscountStatus }> = ({ status }) => {
  const label = REFERRAL_DISCOUNT_STATUS_LABELS[status];
  switch (status) {
    case 'available':
      return <span className={`${BADGE} bg-green-100 text-green-800`}>{label}</span>;
    case 'reserved':
      return <span className={BADGE} style={{ background: '#fef3c7', color: '#92400e' }}>{label}</span>;
    case 'redeemed':
      return <span className={`${BADGE} bg-blue-100 text-blue-800`}>{label}</span>;
    default:
      return <span className={`${BADGE} bg-gray-100 text-gray-800`}>{label}</span>;
  }
};

export const ReferralStatusBadge: React.FC<{ status: ReferralStatus }> = ({ status }) => {
  const label = REFERRAL_STATUS_LABELS[status];
  if (status === 'active') return <span className={`${BADGE} bg-green-100 text-green-800`}>{label}</span>;
  if (status === 'signed_up') return <span className={`${BADGE} bg-gray-100 text-gray-800`}>{label}</span>;
  return <span className={BADGE} style={{ background: '#fef3c7', color: '#92400e' }}>{label}</span>;
};

export const formatReferralDate = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime())
    ? null
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const useUrlFilters = <K extends string>(keys: readonly K[]) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = Object.fromEntries(keys.map(key => [key, searchParams.get(key) || ''])) as Record<K, string>;

  const parsedPage = parseInt(searchParams.get('page') || '1', 10);
  const page = isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;

  const setFilters = (changes: Partial<Record<K, string>>) => {
    setSearchParams(prev => {
      (Object.entries(changes) as [K, string][]).forEach(([key, value]) => {
        if (value) prev.set(key, value);
        else prev.delete(key);
      });
      prev.set('page', '1');
      return prev;
    });
  };

  const setPage = (next: number) => {
    setSearchParams(prev => {
      prev.set('page', String(next));
      return prev;
    });
  };

  return { values, page, setFilters, setPage };
};

export const ListPagination: React.FC<{ page: number; totalPages: number; onPageChange: (page: number) => void }> = ({
  page,
  totalPages,
  onPageChange,
}) => (
  <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
    <p className="text-sm text-gray-700">
      Showing page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span>
    </p>
    {totalPages > 1 && (
      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
        >
          Next
        </button>
      </nav>
    )}
  </div>
);

export const SummaryTile: React.FC<{ value: React.ReactNode; label: React.ReactNode; valueClassName?: string }> = ({
  value,
  label,
  valueClassName = 'text-gray-900',
}) => (
  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
    <p className={`text-2xl font-bold ${valueClassName}`}>{value}</p>
    <p className="text-sm text-gray-600 mt-1">{label}</p>
  </div>
);
